import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  HardHat,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
} from 'lucide-react';
import type {
  InterventionPriorite,
  InterventionStatut,
  InterventionTypeDemande,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { InterventionDetailDrawer } from '../components/interventions/InterventionDetailDrawer';
import {
  PrioriteBadge,
  StatutInterventionBadge,
  formatDuration,
  typeDemandeLabel,
} from '../components/interventions/badges';
import {
  CloturerModal,
  DemarrerModal,
  PauseModal,
  PlanifierModal,
} from '../components/interventions/WorkflowModals';

/**
 * Page `/mes-interventions` (vue technicien — US-INT-06).
 *
 *  - Charge `/interventions?technicien_affecte_id=current_user_id`.
 *  - 3 onglets : Affectées / Planifiées / En cours.
 *  - Cartes mobile-first avec boutons d'action contextuels.
 */

interface InterventionRow {
  id: string;
  numero_demande: string;
  titre: string;
  type_demande: InterventionTypeDemande;
  priorite: InterventionPriorite;
  statut: InterventionStatut;
  date_demande: string;
  date_planifiee: string | null;
  duree_estimee: number | null;
  demandeur: { id: string; first_name: string; last_name: string } | null;
  service_demandeur: { id: string; nom_complet: string } | null;
  equipement: { id: string; code_equipement: string; designation: string } | null;
}

type TabKey = 'affectees' | 'planifiees' | 'en-cours';

const TABS: Array<{ key: TabKey; label: string; statuts: InterventionStatut[] }> = [
  { key: 'affectees', label: 'Affectées', statuts: ['affecte'] },
  { key: 'planifiees', label: 'Planifiées', statuts: ['planifie'] },
  {
    key: 'en-cours',
    label: 'En cours',
    statuts: ['en_cours', 'en_attente_pieces'],
  },
];

export function MesInterventionsPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');

  const [items, setItems] = useState<InterventionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('affectees');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [modal, setModal] = useState<null | {
    kind: 'planifier' | 'demarrer' | 'pause' | 'cloturer';
    id: string;
  }>(null);

  async function refresh() {
    if (!user) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: InterventionRow[] }>(
        `/interventions?technicien_affecte_id=${user.id}&page_size=200&sort_by=date_planifiee&sort_order=asc`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canRead && user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, user]);

  const filteredItems = useMemo(() => {
    const tabCfg = TABS.find((t) => t.key === tab);
    if (!tabCfg) return [];
    return items.filter((it) => tabCfg.statuts.includes(it.statut));
  }, [items, tab]);

  const counts = useMemo(() => {
    const m = new Map<TabKey, number>();
    for (const tabCfg of TABS) {
      m.set(tabCfg.key, items.filter((it) => tabCfg.statuts.includes(it.statut)).length);
    }
    return m;
  }, [items]);

  async function handleReprendre(id: string) {
    try {
      await api.post(`/interventions/${id}/reprendre`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/interventions"
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 mb-1"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Retour aux interventions
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HardHat className="w-7 h-7 mr-2 text-primary-600" />
            Mes interventions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Vos interventions affectées, planifiées et en cours.
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Rafraîchir
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="border-b border-gray-200 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <nav className="flex">
          {TABS.map((tcfg) => {
            const active = tab === tcfg.key;
            const count = counts.get(tcfg.key) ?? 0;
            return (
              <button
                key={tcfg.key}
                onClick={() => setTab(tcfg.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'text-primary-600 border-primary-500 bg-primary-50/50'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                {tcfg.label}
                <span
                  className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold rounded-full ${
                    active ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Aucune intervention dans cet onglet.
        </div>
      ) : (
        <ul className="space-y-3">
          {filteredItems.map((it) => (
            <li key={it.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => setDetailId(it.id)}
                    className="text-xs font-semibold text-primary-600 uppercase tracking-wide hover:underline"
                  >
                    {it.numero_demande}
                  </button>
                  <p className="text-base font-semibold text-gray-900">{it.titre}</p>
                </div>
                <PrioriteBadge priorite={it.priorite} compact />
              </div>

              <div className="flex items-center flex-wrap gap-2 mb-2">
                <StatutInterventionBadge statut={it.statut} />
                <span className="text-xs text-gray-500">{typeDemandeLabel(it.type_demande)}</span>
              </div>

              <dl className="text-xs text-gray-700 space-y-0.5 mb-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Service</dt>
                  <dd>{it.service_demandeur?.nom_complet ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Équipement</dt>
                  <dd className="truncate max-w-[60%]">{it.equipement?.designation ?? '—'}</dd>
                </div>
                {it.date_planifiee && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 inline-flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Planifiée
                    </dt>
                    <dd>{new Date(it.date_planifiee).toLocaleString()}</dd>
                  </div>
                )}
                {it.duree_estimee !== null && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 inline-flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      Durée
                    </dt>
                    <dd>{formatDuration(it.duree_estimee)}</dd>
                  </div>
                )}
              </dl>

              <div className="flex flex-wrap gap-2">
                {it.statut === 'affecte' && (
                  <>
                    <button
                      onClick={() => setModal({ kind: 'planifier', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-xs font-medium"
                    >
                      <Calendar className="w-3.5 h-3.5 mr-1" />
                      Planifier
                    </button>
                    <button
                      onClick={() => setModal({ kind: 'demarrer', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-medium"
                    >
                      <PlayCircle className="w-3.5 h-3.5 mr-1" />
                      Démarrer
                    </button>
                  </>
                )}
                {it.statut === 'planifie' && (
                  <>
                    <button
                      onClick={() => setModal({ kind: 'demarrer', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-medium"
                    >
                      <PlayCircle className="w-3.5 h-3.5 mr-1" />
                      Démarrer
                    </button>
                    <button
                      onClick={() => setModal({ kind: 'planifier', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium"
                    >
                      <Calendar className="w-3.5 h-3.5 mr-1" />
                      Replanifier
                    </button>
                  </>
                )}
                {it.statut === 'en_cours' && (
                  <>
                    <button
                      onClick={() => setModal({ kind: 'pause', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium"
                    >
                      <PauseCircle className="w-3.5 h-3.5 mr-1" />
                      Pause
                    </button>
                    <button
                      onClick={() => setModal({ kind: 'cloturer', id: it.id })}
                      className="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium"
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />
                      Clôturer
                    </button>
                  </>
                )}
                {it.statut === 'en_attente_pieces' && (
                  <button
                    onClick={() => handleReprendre(it.id)}
                    className="inline-flex items-center px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs font-medium"
                  >
                    <PlayCircle className="w-3.5 h-3.5 mr-1" />
                    Reprendre
                  </button>
                )}
                <button
                  onClick={() => setDetailId(it.id)}
                  className="ml-auto inline-flex items-center px-3 py-1.5 border border-gray-300 hover:bg-gray-50 rounded-lg text-xs font-medium text-gray-700"
                >
                  Détail
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal?.kind === 'planifier' && (
        <PlanifierModal
          interventionId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
      {modal?.kind === 'demarrer' && (
        <DemarrerModal
          interventionId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
      {modal?.kind === 'pause' && (
        <PauseModal
          interventionId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}
      {modal?.kind === 'cloturer' && (
        <CloturerModal
          interventionId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      )}

      {detailId && (
        <InterventionDetailDrawer
          interventionId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refresh}
        />
      )}
    </div>
  );
}
